// Optimistic edge-deletion tombstones.
//
// When the user deletes an edge we want it gone from the canvas in the same
// frame, regardless of network state. The problem: several code paths re-apply
// edge lists after a delete (the controlled-prop → store sync, the server
// reconcile, autosave-driven re-renders). Any of them can resurrect a
// just-deleted edge before the backend confirms the deletion — which is why
// deletion only "stuck" once the server responded.
//
// A tombstone records the id of an edge that was optimistically removed. Every
// write to the ReactFlow canvas store is filtered through `filterTombstoned`,
// so a tombstoned edge can never be rendered again until the tombstone is
// cleared (on confirmed deletion, or on a fresh flow load). This makes the
// removal network-independent and uniform across every deletion entry point.

const tombstones = new Set<string>();

export const addEdgeTombstones = (ids: string[]): void => {
  for (const id of ids) {
    if (id) tombstones.add(id);
  }
};

export const clearEdgeTombstones = (ids?: string[]): void => {
  if (!ids) {
    tombstones.clear();
    return;
  }
  for (const id of ids) {
    tombstones.delete(id);
  }
};

export const hasEdgeTombstones = (): boolean => tombstones.size > 0;

export const filterTombstoned = <T extends { id: string }>(edges: T[]): T[] => {
  if (tombstones.size === 0) return edges;
  return edges.filter(edge => !tombstones.has(edge.id));
};
